import { ArrowRight, Receipt } from "lucide-react";
import { useMemo } from "react";

import { ColocaButton } from "@/shared/components/buttons/ColocaButton";
import { ColocaLineSkeleton, ColocaSkeleton } from "@/shared/components/feedback/ColocaSkeleton";
import useTranslations from "@/shared/lang/useTranslations";

import type { CardTransaction } from "../types/card-movements.types";
import { visibleCardMovements } from "../utils/cardMovementHelpers";
import { CardMovementRow } from "./CardMovementRow";

type CardRecentMovementsProps = {
  /** The first page `CardDetailPage` already reads for the Movements tab: no request of its own. */
  movements: CardTransaction[];
  isLoading?: boolean;
  onViewAll: () => void;
  onRowClick?: (transactionId: string) => void;
  /** Movements that just arrived by socket. */
  highlightedIds?: ReadonlySet<string>;
};

const RECENT_MOVEMENTS_COUNT = 3;

export const CardRecentMovements = ({
  movements,
  isLoading = false,
  onViewAll,
  onRowClick,
  highlightedIds,
}: CardRecentMovementsProps) => {
  const { t } = useTranslations("cards");

  // Same filter as the full list, so a purchase reported in two phases is one row here too.
  const rows = useMemo(() => visibleCardMovements(movements).slice(0, RECENT_MOVEMENTS_COUNT), [movements]);

  return (
    <section className="@container overflow-hidden rounded-2xl border border-border bg-card shadow-sm">
      <div className="flex items-center justify-between gap-3 px-4 pb-2 pt-3">
        <h2 className="text-sm font-medium text-foreground">{t("card_detail_recent_title")}</h2>
        {rows.length > 0 && (
          <ColocaButton
            variant="link"
            size="sm"
            iconTrailing={ArrowRight}
            onClick={onViewAll}
            className="px-0 dark:text-emerald-400"
          >
            {t("card_detail_recent_view_all")}
          </ColocaButton>
        )}
      </div>
      <div className="divide-y divide-border border-t border-border">
        {isLoading && rows.length === 0 ? (
          Array.from({ length: RECENT_MOVEMENTS_COUNT }, (_, i) => (
            <div key={i} className="flex items-center gap-3 p-3.5" aria-hidden="true">
              <ColocaSkeleton className="size-9 shrink-0" />
              <div className="min-w-0 flex-1 space-y-1.5">
                <ColocaLineSkeleton className="h-3.5 w-40 max-w-full" />
                <ColocaLineSkeleton className="h-3 w-24" />
              </div>
              <ColocaLineSkeleton className="h-4 w-16" />
            </div>
          ))
        ) : rows.length === 0 ? (
          <div className="flex flex-col items-center gap-1.5 px-4 py-6 text-center">
            <Receipt className="size-5 text-muted-foreground" aria-hidden="true" />
            <p className="text-sm text-muted-foreground">{t("card_detail_recent_empty")}</p>
          </div>
        ) : (
          rows.map((tx) => (
            <CardMovementRow
              key={tx.id}
              movement={tx}
              onClick={onRowClick}
              highlighted={highlightedIds?.has(tx.id)}
            />
          ))
        )}
      </div>
    </section>
  );
};
